import type en from '../en/common'
import type { Translation } from '../types'

/** Words and formats used across the dashboard */
const common: Translation<typeof en> = {
  "common.justNow": "ahora mismo",
  "common.minutesAgo": "hace {count} min",
  "common.hoursAgo": "hace {count} h",
  "common.daysAgo": "hace {count} d",
  "common.duration.minutes": { one: "{count} minuto", other: "{count} minutos" },
  "common.duration.hours": { one: "{count} hora", other: "{count} horas" },
  "common.duration.days": { one: "{count} día", other: "{count} días" },
  "common.duration.weeks": { one: "{count} semana", other: "{count} semanas" },
  "common.cancel": "Cancelar",
  "common.confirm": "Confirmar",
  "common.continue": "Continuar",
  "common.working": "Procesando",
  "common.loading": "Cargando…",
  "common.tryAgain": "No ha funcionado — inténtelo de nuevo",
  "common.tryAgainButton": "Intentar de nuevo",
  "common.retry": "Reintentar",
  "common.copy": "Copiar",
  "common.download": "Descargar",
  "common.saved": "Guardado",
  "common.notSaved": "No guardado",
  "common.couldNotCopy": "No se pudo copiar",
  "common.goBack": "Volver",
  "common.password": "Contraseña",
  "common.emailAddress": "Dirección de correo electrónico",
  "common.showPassword": "Mostrar contraseña",
  "common.hidePassword": "Ocultar contraseña",
  "common.signOut": "Cerrar sesión",
  "common.notFoundTitle": "Página no encontrada",
  "common.notFoundText": "La página que busca no existe o no tiene acceso a ella.",
  "common.errorTitle": "Algo ha salido mal",
  "common.errorUnexpected": "Se ha producido un error inesperado.",
  "common.loadFailed": "No se pudieron cargar los datos.",
  "common.showing": "Mostrando {start}–{end} de {total}",
  "common.previousPage": "Página anterior",
  "common.nextPage": "Página siguiente",
  "common.notifications": "Notificaciones",
  "common.dismiss": "Cerrar",
  "common.refresh": "Actualizar",
  "common.viewAll": "Ver todo",
  "common.view": "Ver",
  "common.delete": "Eliminar",
  "common.close": "Cerrar",
  "common.actions": "Acciones",
  "common.status": "Estado",
  "common.name": "Nombre",
  "common.description": "Descripción",
  "common.clients": "Clientes",
  "common.devices": "Dispositivos",
  "common.active": "Activo",
  "common.disabled": "Desactivado",
  "common.saveFailed": "Error al guardar",
  "common.internalError": "Error interno del servidor",
  "common.clientsCount": { one: "{count} cliente", other: "{count} clientes" },
  "common.devicesCount": { one: "{count} dispositivo", other: "{count} dispositivos" },
  "common.onlineCount": "{count} en línea",
  "common.critical": { one: "{count} crítica", other: "{count} críticas" },
  "common.status.ONLINE": "EN LÍNEA",
  "common.status.OFFLINE": "SIN CONEXIÓN",
  "common.status.WARNING": "ADVERTENCIA",
  "common.status.ERROR": "ERROR",
  "common.status.PENDING": "PENDIENTE",
  "common.status.PENDING_VPN": "ESPERANDO VPN",
  "common.status.PROVISIONING": "EN PREPARACIÓN",
  "common.status.ACTIVE": "ACTIVO",
  "common.status.INACTIVE": "INACTIVO",
  "common.status.COMPLETED": "COMPLETADO",
  "common.status.RUNNING": "EN EJECUCIÓN",
  "common.status.FAILED": "FALLIDO",
  "common.status.EXPIRED": "CADUCADO",
  "common.status.SUSPENDED": "SUSPENDIDO",
  "common.status.DELETED": "ELIMINADO",
  "common.status.FAILOVER": "CONMUTACIÓN",
  "common.status.APPLIED": "APLICADO",
  "common.status.UNSUPPORTED": "NO COMPATIBLE",
  "common.select": "Seleccione…",
  "common.selectAll": "Seleccionar todo",
  "common.deselectAll": "Deseleccionar todo",
  "common.more": "+{count} más",
  "common.optional": "(opcional)",
  "common.sitesCount": { one: "{count} sitio", other: "{count} sitios" },
  "common.send": "Enviar",
  "common.uptime.days": "{d} d {h} h",
  "common.uptime.hours": "{h} h {m} min",
  "common.uptime.minutes": "{m} min",
  "common.copied": "Copiado",
  "common.remove": "Quitar",
  "common.saving": "Guardando…",
  "common.username": "Nombre de usuario",
  "common.tryAgainMoment": "Inténtelo de nuevo en un momento.",
  "common.duration.seconds": { one: "{count} segundo", other: "{count} segundos" },
  "common.status.PENDING_SETUP": "EN CONFIGURACIÓN",
  "common.status.CANCELLED": "CANCELADA",
  "common.languageName.en": "inglés",
  "common.languageName.nl": "neerlandés",
  "common.languageName.fr": "francés",
  "common.languageName.de": "alemán",
  "common.languageName.es": "español",
  "common.status.QUEUED": "GUARDADO PARA MÁS TARDE",
  "common.status.DISCARDED": "DESCARTADO",
  "common.renewCert.title": "¿Renovar el certificado VPN de {name}?",
  "common.renewCert.titleMine": "¿Renovar tu certificado VPN?",
  "common.renewCert.stops": "El certificado actual deja de funcionar al instante: no hay conexión VPN hasta que se instale el archivo nuevo.",
  "common.renewCert.remote": "Una unidad o un controlador al que solo se llega por la VPN deja de ser accesible desde aquí: instalar el archivo nuevo puede requerir a alguien en el lugar.",
  "common.renewCert.noWayBack": "No se puede deshacer: el certificado antiguo no se puede volver a usar.",
  "common.renewCert.continue": "Entendido, continuar",
  "common.renewCert.last": "¿Renovar el certificado ahora? El actual deja de funcionar en cuanto hagas clic.",
  "common.renewCert.renewNow": "Renovar ahora",
  "common.export.button": "Exportar",
  "common.export.title": "Exportar",
  "common.export.what": "Contenido",
  "common.export.summaryNone": "Todo lo que se muestra",
  "common.export.rows": "Filas",
  "common.export.thisPage": "Esta página ({n})",
  "common.export.allMatches": "Todos los resultados ({n})",
  "common.export.tooMany": "Más de 10 000 filas: exporte esta página o acote los filtros",
  "common.export.parts": "Partes",
  "common.export.format": "Formato",
  "common.export.xlsx": "Hoja de cálculo (.xlsx)",
  "common.export.csv": "Hoja de cálculo (CSV)",
  "common.export.pdf": "Informe PDF",
  "common.export.md": "Instrucción en Markdown para Claude Code",
  "common.export.go": "Exportar",
  "common.export.working": "Exportando…",
  "common.export.failed": "No se pudo exportar",
  "common.export.search": "búsqueda «{q}»",
  "common.export.sortedBy": "ordenado por {column}",
  "common.export.sortedByDesc": "ordenado por {column}, descendente",
  "common.export.field": "Campo",
  "common.export.value": "Valor",
  "common.export.deviceInfo": "Información del dispositivo",
  "common.export.siteInfo": "Información del sitio",
  "common.export.traffic": "Tráfico",
  "common.export.cpu": "CPU",
  "common.export.serial": "Número de serie",
  "common.export.mac": "Dirección MAC",
  "common.export.level": "Nivel",
  "common.export.title_": "Título",
  "common.export.message": "Mensaje",
  "common.export.time": "Hora",
  "common.export.security": "Seguridad",
  "common.export.band": "Banda",
  "common.export.vlan": "VLAN",
  "common.export.ssid": "SSID",
  "common.export.enabled": "Activado",
  "common.export.clientCount": "Clientes",
  "common.export.period": "periodo {period}",
  "common.export.range": "del {from} al {to}",
  "common.export.radios": "Radios",
  "common.export.modem": "Módem",
  "common.export.operator": "Operador",
  "common.export.network": "Tipo de red",
  "common.export.events": "Eventos",
  "common.export.controller": "Controlador",
  "common.export.wired": "Cableado",
  "common.export.eventInfo": "Información del evento",
  "common.export.online": "En línea y sin conexión",
  "common.export.loads": "Salidas y devoluciones",
  "common.export.customer": "Cliente",
  "common.export.resolvedAt": "Resuelto",
  "common.export.loadedAt": "Cargado",
  "common.export.loadedBy": "Cargado por",
  "common.export.returnedAt": "Devuelto",
  "common.export.returnedBy": "Devuelto por",
  "common.export.figures": "Cifras",
  "common.export.critical": "Críticas",
  "common.export.vlans": "VLAN",
  "common.export.wifi": "Wi-Fi",
  "common.export.kind": "Tipo",
  "common.export.changedBy": "Modificado por",
  "common.export.changedAt": "Modificado el",
  "common.export.change": "Cambio",
  "common.export.articleInfo": "Datos del artículo",
  "common.export.when": "Cuándo",
  "common.export.copied": "Copiado y descargado",
  "common.export.downloadedOnly": "Descargado (aquí no se permite copiar)",
  "common.export.twoFactor": "Inicio de sesión en dos pasos",
  "common.export.systemRole": "Rol del sistema",
  "common.export.type": "Tipo",
  "common.export.deviceFamily": "Familia de dispositivos",
  "common.export.origin": "Origen",
  "common.export.isDefault": "Predeterminada",
  "common.export.madeBy": "Creado por",
  "common.export.created": "Creado",
  "common.export.applied": "Veces aplicada",
  "common.export.watches": "Vigila",
  "common.export.channels": "Canales",
  "common.export.mutedDevices": "Dispositivos silenciados",
  "common.export.who": "Quién",
  "common.export.action": "Acción",
  "common.export.resource": "Recurso",
  "common.export.health": "Estado",
  "common.export.goesTo": "Lleva a",
  "common.export.checkedAt": "Comprobado",
  "common.export.times": "Veces vistas",
  "common.export.exampleSite": "Sitio de ejemplo",
  "common.export.noLimit": "Sin límite",
  "common.export.lastSeen": "Visto por última vez",
  "common.export.simSlot": "Ranura SIM",
  "common.export.pinTries": "Intentos de PIN restantes",
  "common.export.pukTries": "Intentos de PUK restantes",
  "common.export.pinUsed": "PIN utilizado",
  "common.export.storedPin": "El PIN guardado del router coincide",
  "common.export.readAt": "Leído el",
  "common.export.sims": "Ranuras SIM",
  "common.export.latency": "Latencia (ms)",
  "common.export.loss": "Pérdida (%)",
  "common.export.peer": "Par",
  "common.export.signal": "{name} (dBm)",
}

export default common
